import { ArrowBack, Edit as EditIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Container,
    Grid2 as Grid,
    IconButton,
    LinearProgress,
    Paper,
    Typography,
    useTheme
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ApiClient from '../config/ApiClient';
import { useFocusedAssignment } from '../context/FocusedAssignmentContext';
import { AthletePaymentAssignment, CoachContent, PurposeNames, RelEntity } from '../object-actions/types/types';
import { assignmentHasPurposeEntitlement } from '../subscriptions/purposeEntitlements';
import CoachContentReadOnlyView from './CoachContentReadOnlyView';

const PreAssessmentReview: React.FC = () => {
    const location = useLocation();
    const theme = useTheme();
    const { assignment: focusedAssignment, assignmentId, loading, error } = useFocusedAssignment();

    const [coachContents, setCoachContents] = useState<{
        feedback_report: CoachContent | null;
        talking_points: CoachContent | null;
    }>({
        feedback_report: null,
        talking_points: null,
    });
    const [contentLoading, setContentLoading] = useState(false);

    const assignmentFromState = location.state?.assignment as AthletePaymentAssignment | undefined;
    const assignment = focusedAssignment ?? assignmentFromState ?? null;

    // Fetch CoachContent from API
    const fetchCoachContent = useCallback(async (contentId: number): Promise<CoachContent | null> => {
        try {
            const response = await ApiClient.get<CoachContent>(`/api/coach-content/${contentId}/`);
            if (response.error) {
                console.error(`Error fetching coach content ${contentId}:`, response.error);
                return null;
            }
            return response.success && response.data ? response.data : null;
        } catch (err) {
            console.error(`Error fetching coach content ${contentId}:`, err);
            return null;
        }
    }, []);

    // Load coach content from assignment.content_progress (or coach_progress)
    useEffect(() => {
        if (!assignment) return;

        const loadCoachContents = async () => {
            setContentLoading(true);
            const progress = (assignment as any).coach_progress || assignment.content_progress;

            if (!progress) {
                setContentLoading(false);
                return;
            }

            const purposes: Array<'feedback_report' | 'talking_points'> = [
                'feedback_report',
                'talking_points',
            ];

            const contentPromises = purposes.map(async (purpose) => {
                const contentArray = progress[purpose];
                if (contentArray && Array.isArray(contentArray) && contentArray.length > 0) {
                    // Apply filtering logic: if not coach, only show delivered content
                    let filteredContent = contentArray;

                    if (!assignment.my_roles.includes('coach')) {
                        const now = new Date();
                        filteredContent = contentArray.filter((item: RelEntity<'CoachContent'>) => {
                            const coachDelivered = item.entity?.coach_delivered;
                            return coachDelivered && new Date(coachDelivered) < now;
                        });
                    }

                    if (filteredContent && filteredContent.length > 0) {
                        // Get the first (latest) item's ID
                        const contentId = filteredContent[0].id;
                        if (contentId) {
                            const fullContent = await fetchCoachContent(Number(contentId));
                            return { purpose, content: fullContent };
                        }
                    }
                }
                return { purpose, content: null };
            });

            const results = await Promise.all(contentPromises);
            const newContents: {
                feedback_report: CoachContent | null;
                talking_points: CoachContent | null;
            } = {
                feedback_report: null,
                talking_points: null,
            };

            results.forEach(({ purpose, content }) => {
                if (purpose === 'feedback_report') {
                    newContents.feedback_report = content;
                } else if (purpose === 'talking_points') {
                    newContents.talking_points = content;
                }
            });

            setCoachContents(newContents);
            setContentLoading(false);
        };

        loadCoachContents();
    }, [assignment, fetchCoachContent]);

    // Render an Accordion for a document
    const renderDocumentAccordion = (
        title: string,
        content: CoachContent | null,
        purpose: 'feedback_report' | 'talking_points'
    ) => {
        const hasContent = content && (content.body || content.json_body);

        return (
            <Accordion
                defaultExpanded={hasContent ? true : false}
                elevation={2}
                sx={{
                    mb: 2,
                    '&:before': {
                        display: 'none',
                    },
                    boxShadow: 2,
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(25, 118, 210, 0.1)'
                            : 'rgba(25, 118, 210, 0.05)',
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(25, 118, 210, 0.15)'
                                : 'rgba(25, 118, 210, 0.08)',
                        },
                        '&.Mui-expanded': {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                        minHeight: 64,
                        '& .MuiAccordionSummary-content': {
                            my: 2
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {title}
                        </Typography>
                        {hasContent && content && assignment?.my_roles?.includes('coach')
                            && assignmentHasPurposeEntitlement(assignment, purpose) && (
                            <Button
                                component={Link}
                                to={`/coach-content/${content.id}/?edit=true`}
                                variant="outlined"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ textTransform: 'none' }}
                            >
                                Edit
                            </Button>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {contentLoading ? (
                        <LinearProgress />
                    ) : hasContent && content ? (
                        <CoachContentReadOnlyView entity={content} layout="compact" />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {purpose === 'feedback_report' && 'Review detailed feedback and recommendations based on your assessment results.'}
                            {purpose === 'talking_points' && 'Key discussion points and conversation starters for meaningful family discussions.'}
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>
        );
    };

    if (loading && !assignment) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <LinearProgress />
            </Container>
        );
    }

    if (error && !assignment) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button component={Link} to="/dashboard" startIcon={<ArrowBack />}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    if (!assignment) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No assignment found.
                </Alert>
                <Button component={Link} to="/dashboard" startIcon={<ArrowBack />}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                    component={Link}
                    to={`/program-progress/${assignmentId ?? assignment.paymentassignment_id ?? assignment.id}`}
                    color="secondary"
                >
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4" component="h1">
                    Pre-Assessment Review
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                <Grid container spacing={3}>
                    {/* Left Column - Feedback Report */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        {renderDocumentAccordion(
                            PurposeNames.feedback_report,
                            coachContents.feedback_report,
                            'feedback_report'
                        )}
                    </Grid>

                    {/* Right Column - Talking Points and Scheduling Email */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        {renderDocumentAccordion(
                            PurposeNames.talking_points,
                            coachContents.talking_points,
                            'talking_points'
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );
};

export default PreAssessmentReview;

